import type { SignageComponent } from '@/config/types';
import { widgetSpanStyle } from '@/widgets/widgetSpan';
import { WidgetSlot } from '@/widgets/WidgetSlot';

/**
 * **The one layout model in this app.** A 12-column CSS grid; one cell per component; a
 * component's `span` says how many of the twelve columns it takes, and absent means all
 * twelve.
 *
 * Used by all three containers — `FlatLayout`, `SliderLayout` (per slide) and `GroupWidget` —
 * so a `span` means the same thing wherever a config puts a component, and so there is one
 * place to change when it does not.
 *
 * ## `minmax(0, 1fr)`, never `1fr`
 *
 * The track definition lives in `global.css`, but the reason belongs next to the component
 * that renders into it: a grid track's default minimum is `auto`, i.e. min-content. A Chart.js
 * canvas, an unbroken string or an image with an intrinsic width will therefore push its track
 * past its share and silently break the row — the other widgets on that row get less than
 * their span, or get pushed onto the next one. There are four chart widgets in this app.
 * `minmax(0, 1fr)` on the tracks and `min-width: 0` on the cells are what make a span mean
 * what it says.
 *
 * ## Why the row gap is zero
 *
 * The gutter between two side-by-side widgets is the existing `.component-column` value —
 * `--widget-gutter`, 1.5rem from `lg` up and 0 below — and it is applied as `column-gap` only.
 * Vertical spacing between stacked widgets has always come from `.widget-container`'s own
 * `padding-block` (config data, `layout.components`), so a row gap would add a second source
 * of it and make every one of the seven deployed configs taller. On the three that already
 * overflow their screen that is the opposite of the point.
 */

export type WidgetGridItem = {
  component: SignageComponent;
  /** From `useStableIds()` in the container — never regenerated (CLAUDE.md constraint 3). */
  componentId: string;
  /**
   * Columns out of twelve. Defaults to the full width when absent, so a caller that has no
   * opinion passes `component.span` straight through.
   */
  span?: number;
};

export type WidgetGridProps = {
  items: WidgetGridItem[];
  /** Extra classes on the grid itself, e.g. the legacy `group-container` marker. */
  className?: string;
  /**
   * Class of each cell. Defaults to `widget-container`, which is what carries the configured
   * per-widget padding; `GroupWidget` passes its own so its children are not padded twice.
   */
  cellClassName?: string;
};

export function WidgetGrid({
  items,
  className,
  cellClassName = 'widget-container',
}: WidgetGridProps) {
  return (
    <div className={className === undefined ? GRID_CLASS : `${GRID_CLASS} ${className}`}>
      {items.map(({ component, componentId, span }) => (
        // Keyed by the generated id rather than by the index, which legacy used. The two are
        // one-to-one (the id *is* derived from the index) but the id makes the relationship
        // visible in the DOM, and `data-component-id` is what turns a remote console session
        // into something you can correlate with store keys.
        <div
          className={cellClassName}
          key={componentId}
          data-component-id={componentId}
          style={widgetSpanStyle(span)}
        >
          <WidgetSlot component={component} componentId={componentId} />
        </div>
      ))}
    </div>
  );
}

/**
 * `w-full h-full` are carried over from the flex column this replaced. `h-full` resolves to
 * `auto` in both layouts — no ancestor has a definite height — and that is deliberate: a grid
 * with a definite height would stretch its auto rows to fill the viewport (`align-content`
 * defaults to `stretch`), which would spread the four configs that already fit their screen
 * out across it. Fitting a page that is *too tall* is `useFitToViewport`'s job, and it does it
 * by scaling rather than by redistributing.
 */
const GRID_CLASS = 'widget-grid w-full h-full';
